import { yupResolver } from '@hookform/resolvers/yup';
import { isError } from 'lodash';
import { memo, useCallback, useState } from 'react';
import { Controller, type ControllerFieldState, type ControllerRenderProps, useForm } from 'react-hook-form';
import { type InferType, object, string } from 'yup';

import { useTrackAgentThreadRenamed } from '@air/analytics';
import { InlineError, UnstyledInlineInput } from '@air/component-inline-input';

import { usePrivateWorkspaceAgentSessionUpdateMutation } from '~/components/v2/components/workspace/workspace-agent-panel/queries/mutations/usePrivateWorkspaceAgentSessionUpdateMutation';
import { WORKSPACE_AGENT_SESSION_TITLE_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionUpdate';

const RENAME_SESSION_ERROR = 'Could not rename this task.';

export const PrivateWorkspaceAgentSessionTitleRenameFormValidationSchema = object({
  title: string()
    .trim()
    .required('Task title is required')
    .max(
      WORKSPACE_AGENT_SESSION_TITLE_MAX_LENGTH,
      `Task title must be at most ${WORKSPACE_AGENT_SESSION_TITLE_MAX_LENGTH} characters`,
    ),
});

export type PrivateWorkspaceAgentSessionTitleRenameFormInput = InferType<
  typeof PrivateWorkspaceAgentSessionTitleRenameFormValidationSchema
>;

const validateSessionTitleField = (value: string) => {
  try {
    PrivateWorkspaceAgentSessionTitleRenameFormValidationSchema.validateSync({ title: value });
    return undefined;
  } catch (error) {
    return isError(error) ? error.message : 'Unknown';
  }
};

export type PrivateWorkspaceAgentSessionTitleRenameFormProps = {
  baselineTitle: string;
  defaultTitle: string;
  onCancel: () => void;
  onSuccess: () => void;
  sessionId: string;
  workspaceId: string;
};

export type WorkspaceAgentSessionTitleFieldProps = {
  field: ControllerRenderProps<PrivateWorkspaceAgentSessionTitleRenameFormInput, 'title'>;
  fieldState: ControllerFieldState;
  onCancel: () => void;
  onClearServerError: () => void;
  onSubmit: () => void;
};

type WorkspaceAgentSessionTitleFieldRenderProps = Pick<WorkspaceAgentSessionTitleFieldProps, 'field' | 'fieldState'>;

const WorkspaceAgentSessionTitleField = memo(
  ({ field, fieldState, onCancel, onClearServerError, onSubmit }: WorkspaceAgentSessionTitleFieldProps) => {
    const handleChange = useCallback(
      (value: string) => {
        field.onChange(value);
        onClearServerError();
      },
      [field.onChange, onClearServerError],
    );

    return (
      <UnstyledInlineInput
        allowNewLines={false}
        autoFocus
        className="absolute inset-y-0 right-0 -left-1 z-10"
        data-testid="WORKSPACE_AGENT_SESSION_TITLE_INPUT"
        defaultValue={field.value}
        error={fieldState.error?.message}
        inputClassName="h-full w-full truncate rounded-8 border-none ads:bg-base bg-gray-1 px-1 py-0 font-semibold text-14 leading-5 ads:text-primary text-gray-12 ring-2 ring-transparent ads:focus:ring-focus focus:ring-blue-9 ads:data-[error=true]:ring-error data-[error=true]:ring-red-9 ads:data-[error=true]:focus:ring-error data-[error=true]:focus:ring-red-9"
        label="Task title"
        onCancel={onCancel}
        onChange={handleChange}
        onSubmit={onSubmit}
        rows={1}
        validate={validateSessionTitleField}
      />
    );
  },
);

WorkspaceAgentSessionTitleField.displayName = 'WorkspaceAgentSessionTitleField';

export const PrivateWorkspaceAgentSessionTitleRenameForm = memo(
  ({
    baselineTitle,
    defaultTitle,
    onCancel,
    onSuccess,
    sessionId,
    workspaceId,
  }: PrivateWorkspaceAgentSessionTitleRenameFormProps) => {
    const { isPending, mutateAsync, reset: resetMutation } = usePrivateWorkspaceAgentSessionUpdateMutation();
    const { trackAgentThreadRenamed } = useTrackAgentThreadRenamed();
    const [serverError, setServerError] = useState<string | undefined>();

    const { control, handleSubmit } = useForm<PrivateWorkspaceAgentSessionTitleRenameFormInput>({
      defaultValues: { title: defaultTitle },
      mode: 'onChange',
      resolver: yupResolver(PrivateWorkspaceAgentSessionTitleRenameFormValidationSchema),
    });

    const handleCancel = useCallback(() => {
      resetMutation();
      setServerError(undefined);
      onCancel();
    }, [onCancel, resetMutation]);

    const clearServerError = useCallback(() => {
      setServerError(undefined);
    }, []);

    const onRename = useCallback(
      async ({ title }: PrivateWorkspaceAgentSessionTitleRenameFormInput) => {
        if (isPending) {
          return;
        }

        if (title === baselineTitle) {
          handleCancel();
          return;
        }

        try {
          await mutateAsync({
            agentType: 'workspace',
            sessionId,
            title,
            workspaceId,
          });
          trackAgentThreadRenamed({ name: title });
          resetMutation();
          setServerError(undefined);
          onSuccess();
        } catch {
          setServerError(RENAME_SESSION_ERROR);
        }
      },
      [
        baselineTitle,
        handleCancel,
        isPending,
        mutateAsync,
        onSuccess,
        resetMutation,
        sessionId,
        trackAgentThreadRenamed,
        workspaceId,
      ],
    );

    const handleInlineSubmit = useCallback(() => {
      if (isPending) {
        return;
      }

      void handleSubmit(onRename)();
    }, [handleSubmit, isPending, onRename]);

    const renderTitleField = useCallback(
      ({ field, fieldState }: WorkspaceAgentSessionTitleFieldRenderProps) => (
        <WorkspaceAgentSessionTitleField
          field={field}
          fieldState={fieldState}
          onCancel={handleCancel}
          onClearServerError={clearServerError}
          onSubmit={handleInlineSubmit}
        />
      ),
      [clearServerError, handleCancel, handleInlineSubmit],
    );

    return (
      <>
        <Controller control={control} name="title" render={renderTitleField} />
        <InlineError data-testid="WORKSPACE_AGENT_SESSION_TITLE_SUBMIT_ERROR" error={serverError} />
      </>
    );
  },
);

PrivateWorkspaceAgentSessionTitleRenameForm.displayName = 'PrivateWorkspaceAgentSessionTitleRenameForm';
